import { MAX_MCP_DESCRIPTION_LENGTH, MAX_MCP_NAME_LENGTH, McpServerModel, McpValidationResult } from './McpServerModel';

/**
 * Validate an MCP server name (also its config key / slug): 1–64 chars, lowercase alphanumeric +
 * hyphens, no leading/trailing hyphen, no consecutive `--`. Same rule as skill names so the two
 * managers behave identically, and so the name is safe to use as the mcp-adapters `mcpServers` key.
 */
export function validateMcpName(name: string): McpValidationResult {
    const value = (name ?? '').trim();
    if (!value) {
        return { valid: false, error: 'A name is required.' };
    }
    if (value.length > MAX_MCP_NAME_LENGTH) {
        return { valid: false, error: `Name must be ${MAX_MCP_NAME_LENGTH} characters or fewer.` };
    }
    if (/[A-Z]/.test(value)) {
        return { valid: false, error: 'Use lowercase letters only.' };
    }
    if (!/^[\p{Ll}\p{Nd}-]+$/u.test(value)) {
        return { valid: false, error: 'Use lowercase letters, numbers, and hyphens only.' };
    }
    if (value.startsWith('-') || value.endsWith('-')) {
        return { valid: false, error: 'Name cannot start or end with a hyphen.' };
    }
    if (value.includes('--')) {
        return { valid: false, error: 'Name cannot contain consecutive hyphens.' };
    }
    return { valid: true };
}

/** Validate a server description: non-empty and within the length cap (drives auto-relevance). */
export function validateMcpDescription(description: string): McpValidationResult {
    const value = (description ?? '').trim();
    if (!value) {
        return {
            valid: false,
            error: 'A description is required so the assistant knows when to use this server.',
        };
    }
    if (value.length > MAX_MCP_DESCRIPTION_LENGTH) {
        return { valid: false, error: `Description must be ${MAX_MCP_DESCRIPTION_LENGTH} characters or fewer.` };
    }
    return { valid: true };
}

/**
 * Validate the transport-specific config: stdio needs a non-empty command; http/sse need a well-formed
 * http(s) URL. Shared by the editor (live feedback) and McpService (enforcement).
 */
export function validateTransportConfig(
    input: Pick<McpServerModel, 'transport' | 'command' | 'url'>
): McpValidationResult {
    if (input.transport === 'stdio') {
        if (!(input.command ?? '').trim()) {
            return { valid: false, error: 'A command is required for a stdio (CLI) server.' };
        }
        return { valid: true };
    }
    // http / sse
    const url = (input.url ?? '').trim();
    if (!url) {
        return { valid: false, error: 'A URL is required for an HTTP or SSE server.' };
    }
    let parsed: URL;
    try {
        parsed = new URL(url);
    } catch {
        return { valid: false, error: 'Enter a valid URL (including http:// or https://).' };
    }
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        return { valid: false, error: 'URL must use http:// or https://.' };
    }
    return { valid: true };
}

/**
 * Validate the loopback port an OAuth server listens on for its redirect (`oauthRedirectType:
 * 'localhost'`). Privileged ports are excluded — the app is not elevated and could not bind them.
 */
export function validateMcpOauthLocalhostPort(port?: number): McpValidationResult {
    if (port === undefined || !Number.isInteger(port)) {
        return { valid: false, error: 'Enter a port number.' };
    }
    if (port < 1024 || port > 65535) {
        return { valid: false, error: 'Use a port between 1024 and 65535.' };
    }
    return { valid: true };
}

/**
 * Derive a valid MCP server name/key from free text: lowercase, spaces/underscores → hyphens, strip
 * disallowed chars, collapse repeats, trim hyphens, cap length. Returns '' when nothing usable remains.
 */
export function slugifyMcpName(input: string): string {
    return (input ?? '')
        .toLowerCase()
        .replace(/[_\s]+/g, '-')
        .replace(/[^\p{Ll}\p{Nd}-]/gu, '')
        .replace(/-+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, MAX_MCP_NAME_LENGTH)
        .replace(/-+$/g, '');
}
